import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Typography, IconButton, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';
import { productService } from '../../services/product.service';
import { Product } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try { const data = await productService.getAll(); setProducts(data); }
    catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (selectedProduct?.id) {
      try { await productService.delete(selectedProduct.id); setDeleteDialogOpen(false); loadProducts(); }
      catch (error) { console.error('Error:', error); }
    }
  };

  const columns: GridColDef[] = [
    { field: 'sku', headerName: t('products.sku'), width: 120 },
    { field: 'name', headerName: t('products.product_name'), width: 200 },
    { field: 'categoryName', headerName: t('products.category'), width: 150 },
    { field: 'unitPrice', headerName: t('products.unit_price'), width: 120, type: 'number', renderCell: (p: GridRenderCellParams) => `$${p.value?.toFixed(2)}` },
    { field: 'costPrice', headerName: t('products.cost_price'), width: 120, type: 'number', renderCell: (p: GridRenderCellParams) => `$${p.value?.toFixed(2)}` },
    { field: 'reorderLevel', headerName: t('products.reorder_level'), width: 120, type: 'number' },
    { field: 'unit', headerName: t('products.unit'), width: 80 },
    {
      field: 'isActive', headerName: t('common.status'), width: 100,
      renderCell: (params: GridRenderCellParams) => <Chip label={params.value ? t('common.active') : t('common.inactive')} color={params.value ? 'success' : 'default'} size="small" />,
    },
    {
      field: 'actions', headerName: t('common.actions'), width: 120, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton size="small" onClick={() => navigate(`/products/edit/${params.row.id}`)}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => { setSelectedProduct(params.row); setDeleteDialogOpen(true); }}><Delete fontSize="small" /></IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('products.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/products/new')}>{t('products.new')}</Button>
      </Box>
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid rows={products} columns={columns} loading={loading} pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Paper>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('common.confirm_delete')}</DialogTitle>
        <DialogContent><DialogContentText>{t('products.delete_confirm', { name: selectedProduct?.name || '' })}</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductList;
